import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Table,
  CardFooter,
} from "reactstrap";
// core components
import { useCallback, useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { toast } from "react-toastify";

import Loading from "components/Loading";
import API from "service/api";
import { getExecutionStatusDescription } from "enum/pipeline-status";
import usePagination from "hooks/usepagination";
import PaginationComponent from "components/Pagination/Pagination";

import ListAltIcon from "@mui/icons-material/ListAlt";
import Switch from "@mui/material/Switch";

const Pipeline = () => {
  const { id: pipelineId } = useParams();
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState([]);
  const { setActualPage, actualPage } = usePagination();
  const [offset, setOffset] = useState(actualPage || 1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isRecurrent, setIsRecurrent] = useState(false);
  const [recurrenceActive, setRecurrenceActive] = useState(false);
  const [recurrenceInfo, setRecurrenceInfo] = useState(null);

  useEffect(() => {
    API.get(`/pipeline/${pipelineId}/configuration`)
      .then((response) => {
        const data = response.data;
        setIsRecurrent(!!data.isRecurrent);
        setRecurrenceActive(!!data.recurrenceActive);
        if (data.isRecurrent) {
          setRecurrenceInfo({
            frequency: data.recurrenceFrequency,
            hour: data.recurrenceHour,
            minute: data.recurrenceMinute,
            daysOfWeek: data.recurrenceDaysOfWeek,
            dayOfMonth: data.recurrenceDayOfMonth,
            searchPattern: data.recurrenceSearchPattern,
            cron: data.recurrenceCron,
          });
        }
      })
      .catch(() => {});
  }, [pipelineId]);

  const handleToggleRecurrence = useCallback(async () => {
    const newActive = !recurrenceActive;
    try {
      await API.patch(`/pipeline/${pipelineId}/recurrence`, {
        active: newActive,
      });
      setRecurrenceActive(newActive);
      toast.success(
        newActive ? "Recorrência ativada." : "Recorrência pausada.",
      );
    } catch {
      toast.error("Erro ao alterar recorrência da pipeline.");
    }
  }, [pipelineId, recurrenceActive]);

  const fetchExecutions = useCallback((query) => {
    setLoading(true);

    API.get(`/pipeline/${pipelineId}/execution?${query}`)
      .then((response) => response.data)
      .then((response) => {
        setLoading(false);

        if (!response?.records?.length) {
          toast.warn("Nenhuma execução encontrada para essa pipeline.");
          return;
        }

        setInfo(response.records);
        setTotalCount(response.total);
        setTotalPages(Math.ceil(response.total / 10));
      })
      .catch(() => {
        toast.error(
          "Houve um problema ao tentar recuperar as execuções da pipeline.",
        );
        setLoading(false);
        return;
      });
  }, []);

  const buildQuery = useCallback(() => {
    console.log(offset);
    return `page=${offset}&limit=${10}`;
  }, [offset]);

  useEffect(() => {
    fetchExecutions(buildQuery());
  }, []);

  useEffect(() => {
    setActualPage(offset);
    if (offset !== actualPage) {
      fetchExecutions(buildQuery());
    }
  }, [offset]);

  const frequencyLabels = {
    DAILY: "Diária",
    WEEKLY: "Semanal",
    MONTHLY: "Mensal",
  };

  const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const getNextExecution = (info) => {
    if (!info) return null;
    const now = new Date();
    const { frequency, hour, minute, daysOfWeek, dayOfMonth } = info;

    // hour/minute are in UTC — build dates using UTC methods
    const buildDate = (d) => {
      const date = new Date(d);
      date.setUTCHours(hour, minute, 0, 0);
      return date;
    };

    if (frequency === "DAILY") {
      const today = buildDate(now);
      return today > now
        ? today
        : buildDate(new Date(now.getTime() + 86400000));
    }

    if (frequency === "WEEKLY" && daysOfWeek?.length) {
      const sorted = [...daysOfWeek].sort((a, b) => a - b);
      for (let i = 0; i < 7; i++) {
        const candidate = new Date(now.getTime() + i * 86400000);
        const utcCandidate = buildDate(candidate);
        if (sorted.includes(utcCandidate.getUTCDay())) {
          if (utcCandidate > now) return utcCandidate;
        }
      }
      const candidate = new Date(now.getTime() + 7 * 86400000);
      return buildDate(candidate);
    }

    if (frequency === "MONTHLY" && dayOfMonth) {
      const thisMonth = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          dayOfMonth,
          hour,
          minute,
        ),
      );
      if (thisMonth > now) return thisMonth;
      return new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth() + 1,
          dayOfMonth,
          hour,
          minute,
        ),
      );
    }

    return null;
  };

  const formatSchedule = (info) => {
    if (!info) return "";
    const { frequency, hour, minute, daysOfWeek, dayOfMonth } = info;

    // Convert UTC hour/minute to local time for display
    const utcDate = new Date();
    utcDate.setUTCHours(hour, minute, 0, 0);
    const localHour = String(utcDate.getHours()).padStart(2, "0");
    const localMinute = String(utcDate.getMinutes()).padStart(2, "0");
    const time = `${localHour}:${localMinute}`;

    if (frequency === "DAILY") return `Todo dia às ${time}`;
    if (frequency === "WEEKLY" && daysOfWeek?.length) {
      const days = daysOfWeek.map((d) => dayNames[d]).join(", ");
      return `${days} às ${time}`;
    }
    if (frequency === "MONTHLY" && dayOfMonth) {
      return `Dia ${dayOfMonth} de cada mês às ${time}`;
    }
    return time;
  };

  return (
    <>
      {/* Page content */}
      <Container className="mt-7" fluid>
        <Col className="" xl="12">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">Pipeline {pipelineId} </h3>
                </Col>
                <Col
                  className="text-right d-flex align-items-center justify-content-end"
                  xs="4"
                >
                  {isRecurrent && (
                    <div className="d-flex align-items-center">
                      <span
                        style={{
                          fontSize: "0.85rem",
                          marginRight: "8px",
                          color: recurrenceActive ? "#2dce89" : "#adb5bd",
                        }}
                      >
                        {recurrenceActive
                          ? "Recorrência ativa"
                          : "Recorrência pausada"}
                      </span>
                      <Switch
                        checked={recurrenceActive}
                        onChange={handleToggleRecurrence}
                        size="small"
                        color="primary"
                      />
                    </div>
                  )}
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Row>
                <Col sm={12} md={12} lg={12} className="mb-3 mt-1">
                  <h1
                    style={{
                      fontWeight: "500",
                    }}
                  >
                    Histórico de execuções
                  </h1>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col sm={12} md={12} lg={4}>
                  <Card>
                    <CardBody className="py-2 px-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <span
                          className="text-muted text-uppercase mb-0"
                          style={{ fontSize: "0.8rem" }}
                        >
                          Execuções encontradas
                        </span>
                        <span
                          className="mb-0 font-weight-semibold"
                          style={{ fontSize: "0.8rem" }}
                        >
                          {totalCount}
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              {isRecurrent && recurrenceInfo && (
                <Row className="mb-3">
                  <Col sm={12}>
                    <Card
                      style={{
                        border: "1px solid #e9ecef",
                        borderLeft: `4px solid ${recurrenceActive ? "#5e72e4" : "#adb5bd"}`,
                      }}
                    >
                      <CardBody className="py-3 px-4">
                        <div className="d-flex align-items-center mb-2">
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: "0.9rem",
                              color: "#32325d",
                            }}
                          >
                            Execução recorrente
                          </span>
                          <span
                            className="ml-2 px-2 py-0"
                            style={{
                              fontSize: "0.75rem",
                              borderRadius: "10px",
                              backgroundColor: recurrenceActive
                                ? "#d4edda"
                                : "#f8d7da",
                              color: recurrenceActive ? "#155724" : "#721c24",
                            }}
                          >
                            {recurrenceActive ? "Ativa" : "Pausada"}
                          </span>
                        </div>
                        <Row>
                          <Col sm={6} md={3}>
                            <div
                              style={{ fontSize: "0.78rem", color: "#8898aa" }}
                            >
                              Frequência
                            </div>
                            <div
                              style={{ fontSize: "0.85rem", fontWeight: 500 }}
                            >
                              {frequencyLabels[recurrenceInfo.frequency] ||
                                recurrenceInfo.frequency}
                            </div>
                          </Col>
                          <Col sm={6} md={3}>
                            <div
                              style={{ fontSize: "0.78rem", color: "#8898aa" }}
                            >
                              Agendamento
                            </div>
                            <div
                              style={{ fontSize: "0.85rem", fontWeight: 500 }}
                            >
                              {formatSchedule(recurrenceInfo)}
                            </div>
                          </Col>
                          <Col sm={6} md={3}>
                            <div
                              style={{ fontSize: "0.78rem", color: "#8898aa" }}
                            >
                              Busca de dados
                            </div>
                            <div
                              style={{ fontSize: "0.85rem", fontWeight: 500 }}
                            >
                              {recurrenceInfo.searchPattern || "—"}
                            </div>
                          </Col>
                          <Col sm={6} md={3}>
                            <div
                              style={{ fontSize: "0.78rem", color: "#8898aa" }}
                            >
                              Próxima execução
                            </div>
                            <div
                              style={{ fontSize: "0.85rem", fontWeight: 500 }}
                            >
                              {recurrenceActive
                                ? (() => {
                                    const next =
                                      getNextExecution(recurrenceInfo);
                                    return next
                                      ? next.toLocaleString("pt-BR", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                      : "—";
                                  })()
                                : "Pausada"}
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
              <Card className="shadow">
                <Table className="align-items-center table-flush" responsive>
                  {loading ? <Loading /> : ""}
                  <thead className="thead-light">
                    <tr>
                      <th>Execução</th>
                      <th>Status</th>
                      <th>Cliente</th>
                      <th>Iniciada em</th>
                      <th>Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <NavLink
                            to={`/admin/pipeline-execution/${pipelineId}?executionId=${item.id}`}
                            activeClassName="active"
                          >
                            <div
                              style={{
                                maxWidth: "70px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                              }}
                            >
                              <ListAltIcon />
                              <span
                                style={{ fontSize: "0.75em", marginTop: "4px" }}
                              >
                                Visualizar execução
                              </span>
                            </div>
                          </NavLink>
                        </td>
                        <td>{getExecutionStatusDescription(item?.status)}</td>
                        <td>{item?.client_name ?? "N/A"}</td>
                        <td>
                          {item.started_at
                            ? new Date(item.started_at).toLocaleString(
                                "pt-BR",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )
                            : "N/A"}
                        </td>
                        <td>
                          {new Date(item.created_at).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <CardFooter className="py-4">
                  <nav aria-label="...">
                    <PaginationComponent
                      limit={10}
                      offset={offset}
                      total={totalPages}
                      setOffset={setOffset}
                      maxReached={info.length < 10}
                    />
                  </nav>
                </CardFooter>
              </Card>
            </CardBody>
          </Card>
        </Col>
      </Container>
    </>
  );
};

export default Pipeline;
